import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Eye, Wrench } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { EquipmentDto, CreateEquipmentDto, UpdateEquipmentDto, EquipmentType, EquipmentStatus } from '../types';
import DataGrid from '../components/grid/DataGrid';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import { formatCurrency } from '../utils/formatters';

const EquipmentPage: React.FC = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);
	const [sortField, setSortField] = useState<string>('name');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedEquipment, setSelectedEquipment] = useState<EquipmentDto | null>(null);
	const [equipmentTypeFilter, setEquipmentTypeFilter] = useState<EquipmentType | 'all'>('all');
	const [statusFilter, setStatusFilter] = useState<EquipmentStatus | 'all'>('all');
	const [clientFilter, setClientFilter] = useState<number | 'all'>('all');
	const [clientLocationFilter, setClientLocationFilter] = useState<number | 'all'>('all');
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	// Fetch equipment data
	const {
		data: equipmentResponse,
		isLoading,
		error,
		refetch
	} = useQuery({
		queryKey: ['equipment', currentPage, pageSize, searchTerm, sortField, sortDirection, equipmentTypeFilter, statusFilter, clientLocationFilter],
		queryFn: () => apiService.getEquipment(
			currentPage,
			pageSize,
			clientLocationFilter !== 'all' ? clientLocationFilter : undefined,
			equipmentTypeFilter !== 'all' ? equipmentTypeFilter : undefined,
			searchTerm,
			sortField,
			sortDirection
		),
	});

	// Fetch clients for the filter
	const { data: clientsData } = useQuery({
		queryKey: ['clients-for-equipment-filter'],
		queryFn: async () => {
			const clientsResponse = await apiService.getClients(1, 1000);
			return clientsResponse.data?.data || [];
		},
	});

	const clients = clientsData || [];

	// Fetch all client locations with client info using batch endpoint
	const { data: locationsData } = useQuery({
		queryKey: ['all-client-locations', clients.map(c => c.id)],
		queryFn: async () => {
			if (clients.length === 0) return [];
			
			try {
				// Use batch endpoint to fetch all locations in a single request
				const clientIds = clients.map(c => c.id);
				const batchResponse = await apiService.getClientLocationsBatch(clientIds);
				
				if (batchResponse.success && batchResponse.data) {
					// Flatten the dictionary into an array with client info
					const locations: any[] = [];
					Object.entries(batchResponse.data).forEach(([clientIdStr, clientLocations]) => {
						const clientId = parseInt(clientIdStr);
						const client = clients.find(c => c.id === clientId);
						if (client && Array.isArray(clientLocations)) {
							const mappedLocations = clientLocations.map((loc: any) => ({
								id: loc.id,
								locationName: loc.locationName || loc.name,
								clientId: client.id,
								clientName: client.name
							}));
							locations.push(...mappedLocations);
						}
					});
					console.log(`All locations (batch):`, locations);
					return locations;
				}
				return [];
			} catch (error) {
				console.error('Error fetching locations batch:', error);
				return [];
			}
		},
		enabled: clients.length > 0, // Only run when we have clients
	});

	const allLocations = locationsData || [];

	// Filter locations by selected client
	const filteredLocations = useMemo(() => {
		if (clientFilter === 'all') {
			return allLocations;
		}
		return allLocations.filter(loc => loc.clientId === clientFilter);
	}, [allLocations, clientFilter]);

	// Delete equipment mutation
	const deleteEquipmentMutation = useMutation({
		mutationFn: (id: number) => apiService.deleteEquipment(id),
		onSuccess: (response) => {
			if (response.success) {
				queryClient.invalidateQueries({ queryKey: ['equipment'] });
				setIsDeleteModalOpen(false);
				setSelectedEquipment(null);
				toast.success('Equipment deleted successfully!');
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error deleting equipment:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error deleting equipment: ${errorMessage}`);
		}
	});

	const handleDelete = (equipment: EquipmentDto) => {
		setSelectedEquipment(equipment);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = () => {
		if (selectedEquipment) {
			deleteEquipmentMutation.mutate(selectedEquipment.id);
		}
	};

	const handleView = (equipment: EquipmentDto) => {
		navigate(`/equipment/${equipment.id}`);
	};

	const handleEdit = (equipment: EquipmentDto) => {
		navigate(`/equipment/${equipment.id}/edit`);
	};

	const handleAdd = () => {
		navigate('/equipment/create');
	};

	const getEquipmentTypeDisplay = (type: EquipmentType): string => {
		const typeMap: Record<EquipmentType, string> = {
			[EquipmentType.HVAC]: 'HVAC',
			[EquipmentType.Refrigeration]: 'Refrigeration',
			[EquipmentType.Plumbing]: 'Plumbing',
			[EquipmentType.Electrical]: 'Electrical',
			[EquipmentType.FireSafety]: 'Fire Safety',
			[EquipmentType.Kitchen]: 'Kitchen',
			[EquipmentType.Lighting]: 'Lighting',
			[EquipmentType.BuildingSystems]: 'Building Systems',
			[EquipmentType.Other]: 'Other',
		};
		return typeMap[type] || 'Unknown';
	};

	const getStatusDisplay = (status: EquipmentStatus): string => {
		const statusMap: Record<EquipmentStatus, string> = {
			[EquipmentStatus.Active]: 'Active',
			[EquipmentStatus.Retired]: 'Retired',
			[EquipmentStatus.UnderWarranty]: 'Under Warranty',
		};
		return statusMap[status] || 'Unknown';
	};

	const getStatusColor = (status: EquipmentStatus): string => {
		const colorMap: Record<EquipmentStatus, string> = {
			[EquipmentStatus.Active]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
			[EquipmentStatus.Retired]: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
			[EquipmentStatus.UnderWarranty]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
		};
		return colorMap[status] || 'bg-gray-100 text-gray-800';
	};

	const columns = useMemo(() => [
		{
			id: 'equipmentTypeIcon',
			header: '',
			cell: (value: string, equipment: EquipmentDto) => (
				<span className="text-xl">{equipment.equipmentTypeIcon}</span>
			),
		},
		{
			id: 'name',
			header: 'Name',
			sortable: true,
			cell: (value: string, equipment: EquipmentDto) => (
				<div className="flex flex-col">
					<span className="font-medium text-gray-900 dark:text-gray-100">{equipment.name}</span>
					<span className="text-sm text-gray-500 dark:text-gray-400">{equipment.equipmentTypeDisplay}</span>
				</div>
			),
		},
		{
			id: 'clientLocationName',
			header: 'Location',
			sortable: true,
			cell: (value: string, equipment: EquipmentDto) => (
				<div className="flex flex-col">
					<span className="text-gray-900 dark:text-gray-100">{equipment.clientLocationName}</span>
					{equipment.clientName && (
						<span className="text-sm text-gray-500 dark:text-gray-400">{equipment.clientName}</span>
					)}
				</div>
			),
		},
		{
			id: 'make',
			header: 'Make/Model',
			cell: (value: string, equipment: EquipmentDto) => (
				<div className="flex flex-col">
					{equipment.make && <span className="text-gray-900 dark:text-gray-100">{equipment.make}</span>}
					{equipment.model && <span className="text-sm text-gray-500 dark:text-gray-400">{equipment.model}</span>}
				</div>
			),
		},
		{
			id: 'status',
			header: 'Status',
			sortable: true,
			cell: (value: EquipmentStatus, equipment: EquipmentDto) => (
				<span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(equipment.status)}`}>
					{equipment.statusDisplay}
				</span>
			),
		},
		{
			id: 'workOrderCount',
			header: 'Work Orders',
			sortable: true,
			cell: (value: number, equipment: EquipmentDto) => (
				<span className="text-gray-900 dark:text-gray-100">{equipment.workOrderCount}</span>
			),
		},
		{
			id: 'totalMaintenanceCost',
			header: 'Total Cost',
			sortable: true,
			cell: (value: number, equipment: EquipmentDto) => (
				<div className="relative text-right">
					<div className="group-hover:opacity-0 transition-opacity duration-200">
						<span className="text-gray-900 dark:text-gray-100">
							{equipment.totalMaintenanceCost > 0 ? formatCurrency(equipment.totalMaintenanceCost) : '-'}
						</span>
					</div>
					<div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e) => e.stopPropagation()}>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleView(equipment);
							}}
							className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
							title="View Equipment"
						>
							<Eye className="h-4 w-4" />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleEdit(equipment);
							}}
							className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
							title="Edit Equipment"
						>
							<Edit className="h-4 w-4" />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleDelete(equipment);
							}}
							className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
							title="Delete Equipment"
						>
							<Trash2 className="h-4 w-4" />
						</button>
					</div>
				</div>
			),
		},
	], [handleView, handleEdit, handleDelete]);

	const equipment = equipmentResponse?.data?.data || [];
	const totalCount = equipmentResponse?.data?.totalCount || 0;
	const totalPages = Math.ceil(totalCount / pageSize);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
		setCurrentPage(1);
	};

	if (error) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="text-red-600 dark:text-red-400 text-lg font-medium">Error loading equipment</div>
					<div className="text-gray-500 dark:text-gray-400 mt-2">Please try again</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex justify-between items-center">
				<div>
					<div className="flex items-center space-x-2">
						<Wrench className="h-6 w-6 text-gray-900 dark:text-gray-100" />
						<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Equipment</h1>
					</div>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage and track your equipment</p>
				</div>
				<button
					onClick={handleAdd}
					className="flex items-center gap-2 px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors"
				>
					<Plus className="w-5 h-5" />
					Add Equipment
				</button>
			</div>

			{/* Filters */}
			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
						{/* Client Filter */}
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Client
							</label>
							<select
								value={clientFilter}
								onChange={(e) => {
									const newClientFilter = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
									setClientFilter(newClientFilter);
									// Reset location filter when client changes
									setClientLocationFilter('all');
									setCurrentPage(1);
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
							>
								<option value="all">All Clients</option>
								{clients.map((client: any) => (
									<option key={client.id} value={client.id}>
										{client.name}
									</option>
								))}
							</select>
						</div>

						{/* Location Filter */}
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Location
							</label>
							<select
								value={clientLocationFilter}
								onChange={(e) => {
									setClientLocationFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
									setCurrentPage(1);
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
								disabled={filteredLocations.length === 0}
							>
								<option value="all">
									{clientFilter === 'all' ? 'All Locations' : 'All Locations for Selected Client'}
								</option>
								{filteredLocations.map((location: any) => (
									<option key={location.id} value={location.id}>
										{clientFilter === 'all' ? `${location.clientName} - ${location.locationName}` : location.locationName}
									</option>
								))}
							</select>
						</div>

						{/* Equipment Type Filter */}
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Equipment Type
							</label>
							<select
								value={equipmentTypeFilter}
								onChange={(e) => {
									setEquipmentTypeFilter(e.target.value as EquipmentType | 'all');
									setCurrentPage(1);
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
							>
								<option value="all">All Types</option>
								{Object.values(EquipmentType).filter(v => typeof v === 'number').map((type) => (
									<option key={type} value={type}>
										{getEquipmentTypeDisplay(type as EquipmentType)}
									</option>
								))}
							</select>
						</div>

						{/* Status Filter */}
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Status
							</label>
							<select
								value={statusFilter}
								onChange={(e) => {
									setStatusFilter(e.target.value as EquipmentStatus | 'all');
									setCurrentPage(1);
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
							>
								<option value="all">All Statuses</option>
								{Object.values(EquipmentStatus).filter(v => typeof v === 'number').map((status) => (
									<option key={status} value={status}>
										{getStatusDisplay(status as EquipmentStatus)}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				{/* Data Grid */}
				<DataGrid
					data={equipment}
					columns={columns}
					searchTerm={searchTerm}
					onSearch={setSearchTerm}
					currentPage={currentPage}
					pageSize={pageSize}
					totalCount={totalCount}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					onSort={handleSort}
					sortField={sortField}
					sortDirection={sortDirection}
					searchPlaceholder="Search equipment by name, make, model..."
					emptyMessage="No equipment found"
					isLoading={isLoading}
					getRowClassName={() => 'group'}
					onRowClick={(row) => handleView(row as EquipmentDto)}
				/>
			</div>

			{/* Delete Confirmation Modal */}
			<ConfirmDialog
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setSelectedEquipment(null);
				}}
				onConfirm={confirmDelete}
				title="Delete Equipment"
				message={`Are you sure you want to delete "${selectedEquipment?.name}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
				isLoading={deleteEquipmentMutation.isPending}
			/>
		</div>
	);
};

export default EquipmentPage;

